import { Component, inject, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { EMPTY, firstValueFrom, Observable } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { SnackbarService } from 'ok-angular-shared/components';
import { AccessGroup } from 'src/app/features/access-groups/access-group.model';
import { AccessGroupService } from '../../services/access-group.service';
import { AccessGroupCustomerNumberManagementComponent } from '../access-group-customer-number-management/access-group-customer-number-management.component';

interface AccessGroupCustomerNumbersModalData {
  accessGroupIdentifier: string;
}

@Component({
  selector: 'adm-access-group-customer-numbers-modal',
  imports: [MatDialogModule, MatIconModule, MatButtonModule, AsyncPipe, AccessGroupCustomerNumberManagementComponent],
  templateUrl: './access-group-customer-numbers-modal.component.html',
  styleUrl: './access-group-customer-numbers-modal.component.scss',
})
export class AccessGroupCustomerNumbersModalComponent {
  dialogRef = inject<MatDialogRef<AccessGroupCustomerNumbersModalComponent>>(MatDialogRef);
  data = inject<AccessGroupCustomerNumbersModalData>(MAT_DIALOG_DATA);

  private accessGroupService = inject(AccessGroupService);
  private snackbarService = inject(SnackbarService);

  public accessGroup = signal<AccessGroup | null>(null);
  public saving = signal(false);

  public accessGroup$: Observable<AccessGroup> = this.accessGroupService.getAccessGroup(this.data.accessGroupIdentifier).pipe(
    tap(accessGroup => {
      this.accessGroup.set(accessGroup);
    }),
    catchError(() => {
      this.snackbarService.error('Fejl ved indlæsning af adgangsgruppe.');
      this.dialogRef.close();
      return EMPTY as Observable<AccessGroup>;
    }),
    shareReplay(1)
  );

  public async handleSaveCustomerNumbers(customerNumbers: number[]) {
    this.saving.set(true);

    const accessGroup = this.accessGroup();

    if (!accessGroup) {
      this.saving.set(false);
      return;
    }

    try {
      await firstValueFrom(
        this.accessGroupService.saveAccessGroupAccess(
          accessGroup.identifier,
          customerNumbers,
          accessGroup.chargingUserIdentifiers ?? [],
          accessGroup.idTagIds ?? []
        )
      );

      this.snackbarService.success('Adgangsbegrænsninger blev gemt.');
      this.dialogRef.close({
        ...accessGroup,
        customerNumbers,
      });
    } catch {
      this.snackbarService.error('Fejl ved ændring af adgangsbegrænsninger.');
      this.saving.set(false);
    }
  }

  public close() {
    this.dialogRef.close();
  }
}
